import {
    Button,
    Field,
    Input,
    makeStyles,
    Toaster,
    useToastController,
    ToastTitle,
    Toast,
    shorthands,
    tokens, Tooltip,
} from '@fluentui/react-components';
import { Add24Regular, SendRegular, ArrowClockwise24Regular } from '@fluentui/react-icons';
import React, { useState } from 'react';
import { useNews } from '../../libs/hooks/useNews';
import { NewsResponse } from '../../libs/services/NewsService';
import { RichTextEditor } from './news-window/RichTextEditor';

const useClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        backgroundColor: tokens.colorNeutralBackground1,
        boxShadow: 'rgb(0 0 0 / 25%) 0 0.2rem 0.4rem -0.075rem',
    },
    header: {
        ...shorthands.borderBottom('1px', 'solid', 'rgb(0 0 0 / 10%)'),
        ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalM),
        backgroundColor: tokens.colorNeutralBackground1,
        display: 'flex',
        flexDirection: 'row',
        boxSizing: 'border-box',
        width: '100%',
        justifyContent: 'space-between',
    },
    title: {
        ...shorthands.gap(tokens.spacingHorizontalM),
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'row',
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.padding(tokens.spacingVerticalL, tokens.spacingHorizontalXXL),
        gap: tokens.spacingVerticalL,
        overflow: 'auto',
        height: '100%',
        '&::-webkit-scrollbar-track': {
            background: tokens.colorNeutralBackground3,
        },
        '&::-webkit-scrollbar-thumb': {
            background: tokens.colorNeutralForeground3,
            borderRadius: '5px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
            background: tokens.colorNeutralForeground2,
        },
    },
    controls: {
        display: 'flex',
        flexDirection: 'row',
        marginTop: tokens.spacingVerticalL,
    },
    essentials: {
        display: 'flex',
        flexDirection: 'row',
        marginLeft: 'auto', // align to right
    },
    formContainer: {
        width: '100%',
        height: '100%',
    },
    buttonContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
    },
    fullWidthInput: {
        width: '100%',
    },
    fullHeightInput: {
        height: '100%',
        minHeight: '400px',
    },
    addButton: {
        minWidth: '32px',
        height: '32px',
    },
    sendButton: {
        marginTop: tokens.spacingVerticalS,
        backgroundColor: tokens.colorBrandForeground1,
        color: tokens.colorNeutralForegroundOnBrand,
        ':hover': {
            backgroundColor: tokens.colorBrandForeground1,
            color: tokens.colorNeutralForegroundOnBrand,
            ':active': {
                backgroundColor: tokens.colorBrandForeground1,
                color: tokens.colorNeutralForegroundOnBrand,
            },
        },
    }
});

export interface News {
    id?: string;
    title: string;
    content: string;
    createdAt?: Date;
    updatedAt?: Date;
}

interface NewsWindowProps {
    newsId?: string;
    selectedNews?: NewsResponse;
    onReloadNews: () => void;
    onCreateNews: () => void;
}

export const NewsWindow: React.FC<NewsWindowProps> = ({ newsId, selectedNews, onReloadNews, onCreateNews }) => {
    const classes = useClasses();
    const news = useNews();
    const { dispatchToast } = useToastController();

    const [newsItem, setNewsItem] = useState<News>({ title: '', content: '' });
    const [isSaving, setIsSaving] = useState(false);
    const [titleError, setTitleError] = useState<string | undefined>(undefined);
    const [contentError, setContentError] = useState<string | undefined>(undefined);

    React.useEffect(() => {
        if (newsId && newsId !== 'new' && selectedNews) {
            setNewsItem({
                id: selectedNews.id,
                title: selectedNews.title,
                content: selectedNews.content,
                createdAt: selectedNews.createdAt,
            });
        } else if (newsId === 'new') {
            setNewsItem({title: '', content: ''});
            setTitleError(undefined);
            setContentError(undefined);
        } else if (newsId === 'deleted') {
            setNewsItem({
                id: 'new',
                title: newsItem.title,
                content: newsItem.content,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newsId, selectedNews]);

    const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setNewsItem({ ...newsItem, title: e.target.value });
    };

    const stripHtml = (html: string): string => {
        const temp = document.createElement('div');
        temp.innerHTML = html;
        return temp.textContent ?? '';
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    function onChange(e: any) {
        const value = (e ?? '') as string;

        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment
        setNewsItem(prev => ({ ...prev, content: value }));
    }

    function saveData() {
        setTitleError(undefined);
        setContentError(undefined);

        let hasError = false;

        if (!stripHtml(newsItem.title.trim())) {
            setTitleError('Bitte geben Sie einen Titel ein.');
            hasError = true;
        }

        const strippedContent = stripHtml(newsItem.content.trim());

        if (!strippedContent.trim()) {
            setContentError('Bitte geben Sie einen News-Text ein.');
            hasError = true;
        }

        if (hasError) {
            return;
        }

        setIsSaving(true);

        news.save({
            id: newsItem.id,
            title: newsItem.title.trim(),
            content: newsItem.content.trim(),
        })
        .then(() => {
            setNewsItem({title: '', content: ''});
            onReloadNews();
        })
        .catch((_) => {
            dispatchToast(
                <Toast>
                    <ToastTitle>Beim Speichern ist ein Fehler aufgetreten</ToastTitle>
                </Toast>,
                { intent: "error", position: "top-end" }
            );
        })
        .finally(() => {
            setIsSaving(false);
        });
    }

    const isSaveDisabled = false;

    return (
        <div className={classes.root}>
            <div className={classes.header}>
                <div className={classes.title}>
                    <Tooltip content="Neue News erstellen" relationship="label" withArrow>
                        <Button
                            shape="square"
                            icon={<Add24Regular />}
                            className={classes.addButton}
                            onClick={onCreateNews}
                            aria-label="Neue News erstellen"
                        >
                            News erstellen
                        </Button>
                    </Tooltip>
                </div>
                <div className={classes.buttonContainer}>
                    <Tooltip content="News aktualisieren" relationship="label" withArrow>
                        <Button
                            className={classes.addButton}
                            shape="square"
                            icon={<ArrowClockwise24Regular />}
                            onClick={(_) => {
                                onReloadNews();
                            }}
                            disabled={isSaving}
                            aria-label="News aktualisieren"
                        />
                    </Tooltip>
                </div>
            </div>
            <div className={classes.content}>
                <div className={classes.formContainer}>
                    <Field
                        label="Titel"
                        required
                        validationMessage={titleError}
                        validationState={titleError ? "error" : "none"}
                    >
                        <Input
                            value={newsItem.title}
                            onChange={handleTitleChange}
                            placeholder="News-Titel eingeben"
                            className={classes.fullWidthInput}
                            maxLength={100}
                        />
                    </Field>

                    <Field
                        label="Inhalt"
                        required
                        validationMessage={contentError}
                        validationState={contentError ? "error" : "none"}
                    >
                        <div className={`${classes.fullHeightInput} ${classes.fullWidthInput}`}>
                            <RichTextEditor
                                value={newsItem.content}
                                onChange={onChange}
                            />
                        </div>
                    </Field>

                    <div className={classes.controls}>
                        <div className={classes.essentials}>
                            <Tooltip content="Senden" relationship="label" withArrow>
                                <Button
                                    appearance="primary"
                                    icon={<SendRegular />}
                                    onClick={() => {
                                        saveData();
                                    }}
                                    disabled={isSaveDisabled}
                                >
                                    Senden
                                </Button>
                            </Tooltip>
                        </div>
                    </div>
                </div>
            </div>
            <Toaster />
        </div>
    );
};
